/**
 * Header Component
 * - Top navigation bar with search functionality
 * - Sidebar toggle controls
 * - User menu and notifications
 * - Global search across users, blogs, and pet finders
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  MdOutlineCalendarToday,
} from "react-icons/md";
import {
  RiMenuFoldFill,
  RiMenuLine,
} from "react-icons/ri";
import { FiSearch } from "react-icons/fi";
import { IoNotificationsOutline } from "react-icons/io5";
import { Menu } from "lucide-react"; // simple hamburger icon for mobile


const Header = ({ toggleSidebar, isSidebarOpen, toggleMobileSidebar }) => {
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Get data from Redux store with safe defaults
  const { petParents = [], veterinarians = [] } = useSelector((state) => state.users || {});
  const { items: blogs = [] } = useSelector((state) => state.blogsCommunity || {});
  const { items: petFinders = [] } = useSelector((state) => state.petFinder || {});
  const { items: consultations = [] } = useSelector((state) => state.consultation || {});

  // console.log('🎯 Header - Search data loaded:', {
  //   petParents: petParents?.length || 0,
  //   blogs: blogs?.length || 0,
  //   petFinders: petFinders?.length || 0,
  //   consultations: consultations?.length || 0
  // });

  // Memoize search data to prevent infinite re-renders
  const searchData = useMemo(() => ({
    petParents,
    veterinarians,
    blogs,
    petFinders,
    consultations
  }), [petParents, veterinarians, blogs, petFinders, consultations]);

  // Search function
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    const results = [];
    const searchTerm = query.toLowerCase();

    // Search in pet parents
    searchData.petParents.forEach(user => {
      if (
        user.name?.toLowerCase().includes(searchTerm) ||
        user.email?.toLowerCase().includes(searchTerm) ||
        user.mobile?.toLowerCase().includes(searchTerm) ||
        user.user_code?.toLowerCase().includes(searchTerm)
      ) {
        results.push({
          id: user.uuid,
          type: 'user',
          title: user.name,
          subtitle: user.email,
          category: 'Pet Parent',
          path: `/swift-app/admin/profile/pet-parents/${user.uuid}`
        });
      }
    });

    // Search in veterinarians
    searchData.veterinarians.forEach(user => {
      if (
        user.name?.toLowerCase().includes(searchTerm) ||
        user.email?.toLowerCase().includes(searchTerm) ||
        user.mobile?.toLowerCase().includes(searchTerm) ||
        user.user_code?.toLowerCase().includes(searchTerm)
      ) {
        results.push({
          id: user.uuid,
          type: 'user',
          title: user.name,
          subtitle: user.email,
          category: 'Veterinarian',
          path: `/swift-app/admin/profile/veterinarians/${user.uuid}`
        });
      }
    });

    // Search in blogs
    searchData.blogs.forEach(blog => {
      if (
        blog.title?.toLowerCase().includes(searchTerm) ||
        blog.author?.name?.toLowerCase().includes(searchTerm) ||
        blog.category?.toLowerCase().includes(searchTerm)
      ) {
        results.push({
          id: blog.uuid,
          type: 'blog',
          title: blog.title,
          subtitle: `By ${blog.author?.name}`,
          category: 'Blog',
          path: `/swift-app/admin/blogsCommunity/${blog.uuid}`
        });
      }
    });

    // Search in pet finders
    searchData.petFinders.forEach(petFinder => {
      const petName = `${petFinder.breed} - ${petFinder.pet_name}`;
      if (
        petName?.toLowerCase().includes(searchTerm) ||
        petFinder.user_details?.name?.toLowerCase().includes(searchTerm) ||
        petFinder.last_known_location?.toLowerCase().includes(searchTerm)
      ) {
        results.push({
          id: petFinder.uuid,
          type: 'pet-finder',
          title: petName,
          subtitle: `Posted by ${petFinder.user_details?.name}`,
          category: 'Pet Finder',
          path: `/swift-app/admin/pet-finder/${petFinder.uuid}`
        });
      }
    });

    // Search in consultations
    searchData.consultations.forEach(consultation => {
      const petName = consultation.pet?.name || 'N/A';
      const ownerName = consultation.booker?.first_name + ' ' + (consultation.booker?.last_name || '');
      if (
        petName?.toLowerCase().includes(searchTerm) ||
        ownerName?.toLowerCase().includes(searchTerm) ||
        consultation.service_provider?.name?.toLowerCase().includes(searchTerm)
      ) {
        results.push({
          id: consultation.uuid,
          type: 'consultation',
          title: `Consultation for ${petName}`,
          subtitle: `Owner: ${ownerName}`,
          category: 'Consultation',
          path: `/swift-app/admin/consultation/${consultation.uuid}`
        });
      }
    });

    setSearchResults(results.slice(0, 10)); // Limit to 10 results
    setShowResults(true);
    setIsSearching(false);
  }, [searchData]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (result) => {
    navigate(result.path);
    setShowResults(false);
    setSearchQuery("");
  };

  return (
    <header className="w-full flex items-center justify-between px-4 lg:px-6 py-3 lg:py-4 bg-white border-b border-gray-200">
      {/* Left section */}
      <div className="flex items-center space-x-3 lg:space-x-4">
        {/* Mobile/Tablet hamburger icon */}
        <button
          onClick={toggleMobileSidebar}
          className="text-2xl text-gray-700 hover:text-gray-900 transition lg:hidden"
        >
          <Menu className="w-6 h-6" />
        </button>

        {/* Desktop sidebar toggle */}
        <button
          onClick={toggleSidebar}
          className="hidden lg:block text-2xl text-gray-700 hover:text-gray-900 transition"
        >
          {isSidebarOpen ? <RiMenuFoldFill /> : <RiMenuLine />}
        </button>

        {/* Search box (desktop only) */}
        <div className="relative hidden lg:block" ref={searchRef}>
          <input
            type="text"
            placeholder="Search users, blogs, pets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 w-72"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl">
            <FiSearch />
          </span>

          {/* Search Results Dropdown */}
          {showResults && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
              {isSearching ? (
                <div className="px-4 py-3 text-gray-500 text-sm">Searching...</div>
              ) : searchResults.length > 0 ? (
                searchResults.map((result) => (
                  <div
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {result.title}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {result.subtitle}
                        </div>
                      </div>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full ml-2">
                        {result.category}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-gray-500 text-sm">No results found</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center space-x-3 lg:space-x-4">
        {/* Calendar icon */}
        <button className="bg-gray-100 w-10 h-10 grid place-content-center rounded-lg text-gray-600 hover:bg-gray-200 transition text-xl lg:text-2xl">
          <MdOutlineCalendarToday />
        </button>

        {/* Notification icon with badge */}
        <div className="relative">
          <button className="text-gray-800 text-2xl hover:text-gray-900 transition">
            <IoNotificationsOutline />
          </button>
          <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center shadow">
            2
          </span>
        </div>

        {/* User avatar and name */}
        <div className="flex items-center space-x-2">
          <img
            src="https://i.pravatar.cc/150?img=3"
            alt="avatar"
            className="w-8 h-8 rounded-full ring-2 ring-blue-200"
          />
          <span className="hidden md:inline text-gray-800 font-medium">
            John Doe
          </span>
        </div>
      </div>
    </header>
  );
};

export default Header;
